/**
 * Family News store.
 *
 * Every read and write goes through here: `GET /family-news`,
 * `GET /family-news/{id}`, `POST /family-news` (create),
 * `PUT /family-news/{id}` (edit), `DELETE /family-news/{id}` (soft delete) and
 * `DELETE /family-news/{id}/images/{image_id}` (drop one photo for good). Only
 * the archive folder names are local — the API has no folder field.
 */
import { getApi, postApi, putApi, deleteApi } from "./apiService";
import { toPersonSummary } from "../Components/AdminComponents/FamilyNews/familyNewsHelpers";

const FOLDERS_KEY = "familyNewsFolders";

/** Entries older than this (measured from the event date) auto-archive. */
export const ARCHIVE_AFTER_DAYS = 30;
/** Folder archived entries land in. Always present, cannot be removed. */
export const DEFAULT_ARCHIVE_FOLDER = "Family Events";

const readStore = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error("familyNewsService: unable to persist", error);
    return false;
  }
};

/** Days between the event date and today; negative for future dates. */
export const daysSince = (dateStr) => {
  if (!dateStr) return 0;
  const eventDay = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(eventDay.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - eventDay) / 86400000);
};

/** Multi-day entries archive from the day the event ends, not the day it starts. */
export const shouldAutoArchive = (entry) =>
  !entry?.archived &&
  daysSince(entry?.end_date || entry?.date) >= ARCHIVE_AFTER_DAYS;

/* ---------------------------------------------------------------------------
 * API -> card shape
 *
 * A row comes back flat with its associations nested and capitalised:
 * `Event_Type`, `Host`, `images[]` and `invitations[].invited_member` (the
 * attendees). Guests are plain strings in `family_guest`, and timestamps are
 * epoch millis held as strings.
 * ------------------------------------------------------------------------ */

const asArray = (value) => (Array.isArray(value) ? value : []);

const text = (value) => (value === undefined || value === null ? "" : String(value));

/** Dates are normalised to YYYY-MM-DD — what the cards and the antd pickers
    read — from the ISO, DD.MM.YYYY or epoch-millis forms the API sends. */
const toDateString = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const raw = String(value).trim();

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dotted = /^(\d{2})[./-](\d{2})[./-](\d{4})$/.exec(raw);
  if (dotted) return `${dotted[3]}-${dotted[2]}-${dotted[1]}`;

  const parsed = /^\d{10,}$/.test(raw) ? new Date(Number(raw)) : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * News records name the family line but never carry its hex code, so the
 * colours are looked up from the member counts endpoint — the same list the
 * add/edit modal filters by — rather than from a hardcoded table.
 */
const getFamilyLineColors = async (familyTreeId) => {
  const colors = new Map();
  try {
    const response = await getApi(
      `/family-members-count-by-filter?family_tree_id=${encodeURIComponent(
        familyTreeId
      )}&type=0`
    );
    asArray(response?.data?.members).forEach((member) => {
      const name = text(member?.color_name).trim().toLowerCase();
      const hex = text(member?.hex_code).trim();
      if (name && hex && !colors.has(name)) colors.set(name, hex);
    });
  } catch (error) {
    // The names still show, just in the default colour.
    console.error("familyNewsService: unable to load line colours", error);
  }
  return colors;
};

/**
 * `invited_member` and `Host` come back with only an id, first name and line —
 * no suffix or special name — so names like "Tony, Jr." would lose their
 * suffix. Those two fields are filled in from the invitation roster (the list
 * the add/edit modal picks attendees from), matched by member id.
 */
const getRosterById = async (familyTreeId) => {
  const roster = new Map();
  try {
    const response = await getApi(
      `/family-members-for-invitation-with-spouses?family_tree_id=${encodeURIComponent(
        familyTreeId
      )}`
    );
    asArray(response?.data?.members).forEach((member) => {
      [member?.id, member?.user_id].forEach((key) => {
        if (key !== undefined && key !== null && !roster.has(String(key))) {
          roster.set(String(key), member);
        }
      });
    });
  } catch (error) {
    // Names still show, just without suffixes.
    console.error("familyNewsService: unable to load member roster", error);
  }
  return roster;
};

const withRosterNameParts = (member, roster) => {
  const match = roster?.get(text(member.id));
  if (!match) return member;
  return {
    ...member,
    suffix: member.suffix || match.suffix,
    special_name: member.special_name || match.special_name,
    user_id: member.user_id || match.user_id,
  };
};

const toPerson = (rawMember, lineColors, roster) => {
  if (!rawMember || typeof rawMember !== "object") return null;
  const member = withRosterNameParts(rawMember, roster);
  const person = toPersonSummary(member);
  if (!member.first_name && !member.name) return null;
  if (person.hex_code === "#000000" && person.color_name) {
    person.hex_code =
      lineColors?.get(person.color_name.trim().toLowerCase()) || "#000000";
  }
  return person;
};

/**
 * Each invitation carries only `user_id`, `family_member_id` (null for some,
 * e.g. in-laws) and a bare `User` { id, name, email }. The full person — line,
 * colour, suffix — comes from the roster, matched by member id then user id;
 * anyone not on the roster still shows by the `User` name.
 */
const toAttendee = (invitation, lineColors, roster) => {
  if (!invitation || typeof invitation !== "object") return null;
  if (Number(invitation.is_deleted) === 1) return null;
  // Older rows nested the member itself.
  if (invitation.invited_member) {
    return toPerson(invitation.invited_member, lineColors, roster);
  }

  const userId = text(invitation.user_id || invitation.User?.id);
  const match =
    roster?.get(text(invitation.family_member_id)) || roster?.get(userId);
  const member = match || {
    id: text(invitation.family_member_id) || userId,
    first_name: text(invitation.User?.name),
    email: invitation.User?.email || null,
  };
  return toPerson({ ...member, user_id: member.user_id || userId }, lineColors, roster);
};

/** The card and the photo viewer both read `dataUrl`, so uploaded photos are
    mapped onto the same shape the file picker produces for new ones. */
const toPhoto = (image, index) => {
  const url = text(image?.image_url || image?.image || image).trim();
  if (!url) return null;
  return {
    id: text(image?.id) || `photo-${index}`,
    name: text(image?.name || image?.file_name),
    dataUrl: url,
  };
};

/** One API row as the cards, the search and the edit modal expect it. */
export const normalizeNewsItem = (row, lineColors, roster) => {
  if (!row || typeof row !== "object") return null;

  const id = text(row.id);
  if (!id) return null;

  const date = toDateString(row.start_date);
  const endDate = toDateString(row.end_date);

  const entry = {
    id,
    family_tree_id: text(row.family_tree_id),
    date,
    // A single-day event repeats its date; the card only shows a range when the
    // two differ.
    end_date: endDate === date ? "" : endDate,
    title_id: text(row.event_type_id),
    title: text(row.title || row.Event_Type?.name),
    theme: text(row.theme),
    location: text(row.location),
    // No dedicated field for the activities yet, so they ride in `description`.
    activities: text(row.participated_activities || row.description),
    // Likewise the honoree's/host's own words — the API has no column for them
    // yet, so the value only survives a save once the backend adds one.
    words: text(row.words_from_honoree || row.words),
    hosts: [toPerson(row.Host, lineColors, roster)].filter(Boolean),
    attendees: asArray(row.invitations)
      .map((invitation) => toAttendee(invitation, lineColors, roster))
      .filter(Boolean),
    guests: asArray(row.family_guest).map((guest) => text(guest).trim()).filter(Boolean),
    photos: asArray(row.images).map(toPhoto).filter(Boolean),
    folder: text(row.folder) || DEFAULT_ARCHIVE_FOLDER,
    created_at: toDateString(row.createdAt),
    updated_at: toDateString(row.updatedAt),
  };

  // Archiving is still worked out on the client: the API has no archive flag.
  return { ...entry, archived: shouldAutoArchive(entry), archived_at: null };
};

/** The interceptor resolves failures with the error response, so anything that
    is not a 200/201 has to be turned back into a throw here. */
const assertOk = (response, fallbackMessage) => {
  if (!response) throw new Error(fallbackMessage);
  const { statusCode } = response;
  if (statusCode !== undefined && statusCode !== 200 && statusCode !== 201) {
    throw new Error(response.message || fallbackMessage);
  }
  return response;
};

/** Newest first. Entries saved without an event date fall back to when they
    were created, so the order stays stable rather than arbitrary. */
const byNewest = (a, b) =>
  new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0);

/** GET /family-news?family_tree_id=... */
export const getFamilyNews = async (familyTreeId) => {
  if (!familyTreeId) return [];
  const [response, lineColors, roster] = await Promise.all([
    getApi(`/family-news?family_tree_id=${encodeURIComponent(familyTreeId)}`),
    getFamilyLineColors(familyTreeId),
    getRosterById(familyTreeId),
  ]);
  assertOk(response, "Failed to load family news");
  return asArray(response?.data)
    .map((row) => normalizeNewsItem(row, lineColors, roster))
    .filter(Boolean)
    .sort(byNewest);
};

/** GET /family-news/{id} */
export const getFamilyNewsById = async (id, familyTreeId) => {
  if (!id) return null;
  const [response, lineColors, roster] = await Promise.all([
    getApi(`/family-news/${id}`),
    familyTreeId ? getFamilyLineColors(familyTreeId) : Promise.resolve(new Map()),
    familyTreeId ? getRosterById(familyTreeId) : Promise.resolve(new Map()),
  ]);
  assertOk(response, "Failed to load this news item");
  const row = Array.isArray(response?.data) ? response.data[0] : response?.data;
  return normalizeNewsItem(row, lineColors, roster);
};

/**
 * POSTs a news entry. The Content-Type is left to axios so the multipart
 * boundary is generated, matching how the Events modal posts its FormData.
 */
export const postFamilyNews = (formData) => postApi("/family-news", formData);

/** PUT /family-news/{id} — the edit path. Photos already on the record are
    removed with `deleteFamilyNewsImage` before this runs, so the body only
    carries the newly attached ones. */
export const putFamilyNews = (id, formData) =>
  putApi(`/family-news/${id}`, formData);

/** DELETE /family-news/{id}/images/{image_id} — permanent, one photo. */
export const deleteFamilyNewsImage = (newsId, imageId) =>
  deleteApi(`/family-news/${newsId}/images/${imageId}`);

/** DELETE /family-news/{id} (soft delete). Returns the API response so the
    caller can show the server's own message. */
export const deleteFamilyNews = (id) => {
  if (!id) return Promise.resolve({ statusCode: 400, message: "Missing news id" });
  return deleteApi(`/family-news/${id}`);
};

/** Folder names for a tree; the default archive folder is always first. */
export const getFolders = (familyTreeId) => {
  if (!familyTreeId) return [DEFAULT_ARCHIVE_FOLDER];
  const store = readStore(FOLDERS_KEY);
  const custom = Array.isArray(store[familyTreeId]) ? store[familyTreeId] : [];
  return [
    DEFAULT_ARCHIVE_FOLDER,
    ...custom.filter((name) => name && name !== DEFAULT_ARCHIVE_FOLDER),
  ];
};

export const addFolder = (familyTreeId, name) => {
  const folderName = String(name || "").trim();
  if (!familyTreeId || !folderName) return getFolders(familyTreeId);
  const store = readStore(FOLDERS_KEY);
  const custom = Array.isArray(store[familyTreeId]) ? store[familyTreeId] : [];
  const exists = [DEFAULT_ARCHIVE_FOLDER, ...custom].some(
    (existing) => existing.toLowerCase() === folderName.toLowerCase()
  );
  if (!exists) {
    store[familyTreeId] = [...custom, folderName];
    writeStore(FOLDERS_KEY, store);
  }
  return getFolders(familyTreeId);
};
